import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function Pricing() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/me`, {
          method: 'GET',
        });

        if (response.ok) {
          const data = await response.json();
          setUserInfo(data);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []);

  const handlePlanSelection = (planType) => {
    if (loadingUserInfo) return;

    if (userInfo) {
      // Authenticated user - go to checkout
      navigate(`/billing/checkout?plan=${planType}&billing=${billingCycle}`);
    } else {
      // Unauthenticated user - go to signup
      navigate(`/signup?plan=${planType}&billing=${billingCycle}`);
    }
  };

  const isCurrentPlan = (planType) => {
    return userInfo && userInfo.plan_type === planType;
  };

  const getPricing = (plan) => {
    const prices = {
      free: { monthly: 0, yearlyMonthlyDisplay: 0 },
      pro: { monthly: 19, yearlyMonthlyDisplay: 17.41 },
      premium: { monthly: 39.99, yearlyMonthlyDisplay: 36.66 }
    };
  
    return billingCycle === 'yearly'
      ? prices[plan].yearlyMonthlyDisplay
      : prices[plan].monthly;
  };
  
  const getPricePeriod = () => {
    return billingCycle === 'yearly' ? 'month' : 'month';
  };

  return (
    <div className="public-page">
      {/* Navigation */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <Link to="/" className="public-nav-link">Home</Link>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
            <Link to="/login" className="public-nav-link">Log in</Link>
          </div>
        </div>
      </nav>

      {/* Pricing Page Content */}
      <div className="pricing-page-content">
        <div className="pricing-header">
          <p className="pricing-kicker">Plans & Pricing</p>
          <h1 className="pricing-title">Choose Your Plan</h1>
          <p className="pricing-subtitle">
            Start free with manual screening, or upgrade for full S&P 500 scans, advanced research, and portfolio tools.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="billing-toggle-container">
          <button
            className={`billing-toggle-option ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            className={`billing-toggle-option ${billingCycle === 'yearly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('yearly')}
          >
            Yearly
            <span className="billing-toggle-badge">Save ~1 month</span>
          </button>
        </div>

        {/* Pricing Cards */}
        <div className="pricing-cards">
          {/* Free Plan */}
          <div className="pricing-card">
            <div className="pricing-card-header">
              <h2 className="pricing-card-title">Free Preview</h2>
              <p className="pricing-card-description">
                Try manual screening with up to 10 tickers at a time.
              </p>
              <div className="pricing-card-price">
                <span className="price-amount">$0</span>
                <span className="price-period">/month</span>
              </div>
            </div>
            <div className="pricing-card-body">
              <ul className="pricing-features">
                <li>Manual scans (up to 10 tickers)</li>
                <li>5 saved scans</li>
                <li>Basic stock research pages</li>
                <li>1 portfolio (10 holdings max)</li>
                <li>1-year price charts</li>
                <li>Core valuation metrics</li>
                <li>Self-service help and FAQs</li>
              </ul>
            </div>
            <div className="pricing-card-footer">
              {isCurrentPlan('free') ? (
                <button className="btn-pricing-current" disabled>
                  Current Plan
                </button>
              ) : (
                <Link to="/signup" className="btn-pricing-outline">
                  Get Started Free
                </Link>
              )}
            </div>
          </div>

          {/* Pro Plan */}
          <div className="pricing-card">
              <div className="pricing-card-header">
              <h2 className="pricing-card-title">Pro</h2>
              <p className="pricing-card-description">
                All major indexes screening, portfolios, alerts, and practical research tools.
              </p>
              <div className="pricing-card-price">
                <span className="price-amount">${getPricing('pro')}</span>
                <span className="price-period">/{getPricePeriod()}</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="pricing-card-billing-note">Billed annually at $209/year (save ~$19)</p>
              )}
            </div>
            <div className="pricing-card-body">
              <ul className="pricing-features">
                <li>Scan All Indexed Tickers, S&P 500, Nasdaq-100, Dow 30, S&P/TSX 60, and S&P/TSX composite</li>
                <li>Refine results by country, sector, and industry</li>
                <li>25 saved scans</li>
                <li>Full stock research pages</li>
                <li>5 portfolios (50 holdings each)</li>
                <li>General alerts across the full universe</li>
                <li>Weekly digest</li>
                <li>Standard PDF reports</li>
                <li>Historical return backtesting</li>
                <li>3Y, 5Y, 10Y price charts</li>
                <li>Full fundamentals data</li>
                <li>48h Email support</li>
              </ul>
            </div>
            <div className="pricing-card-footer">
              {isCurrentPlan('pro') ? (
                <button className="btn-pricing-current" disabled>
                  Current Plan
                </button>
              ) : (
                <button 
                  className="btn-pricing-primary"
                  onClick={() => handlePlanSelection('pro')}
                  disabled={loadingUserInfo}
                >
                  Choose Pro
                </button>
              )}
            </div>
          </div>

          {/* Premium Plan */}
          <div className="pricing-card pricing-card-featured">
            <div className="pricing-card-badge">Most Popular</div>
            <div className="pricing-card-header">
              <h2 className="pricing-card-title">Premium</h2>
              <p className="pricing-card-description">
                Advanced research: Strategy Lab, written interpretations, and portfolio rebalancing.
              </p>
              <div className="pricing-card-price">
                <span className="price-amount">${getPricing('premium')}</span>
                <span className="price-period">/{getPricePeriod()}</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="pricing-card-billing-note">Billed annually at $439.89/year (save ~$40)</p>
              )}
            </div>
            <div className="pricing-card-body">
              <ul className="pricing-features">
                <li>Everything in Pro, plus:</li>
                <li>Strategy Lab</li>
                <li>Optimal Top N Stocks Finder</li>
                <li>Strategy simulation (Balanced/Growth/Conservative)</li>
                <li>Written Investment Score interpretations</li>
                <li>Written fundamentals interpretations</li>
                <li>Written backtesting interpretations</li>
                <li>One-click portfolio rebalancing</li>
                <li>100 saved scans</li>
                <li>10 portfolios (200 holdings each)</li>
                <li>Refined portfolio alerts by country, sector, and industry</li>
                <li>Advanced PDF reports</li>
                <li>Priority support</li>
              </ul>
            </div>
            <div className="pricing-card-footer">
              {isCurrentPlan('premium') ? (
                <button className="btn-pricing-current" disabled>
                  Current Plan
                </button>
              ) : (
                <button 
                  className="btn-pricing-primary"
                  onClick={() => handlePlanSelection('premium')}
                  disabled={loadingUserInfo}
                >
                  Choose Premium
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="pricing-comparison">
          <h2 className="pricing-comparison-title">Compare Plans</h2>
          <div className="pricing-comparison-table">
            <div className="comparison-row comparison-header">
              <div className="comparison-cell">Feature</div>
              <div className="comparison-cell">Free</div>
              <div className="comparison-cell">Pro</div>
              <div className="comparison-cell">Premium</div>
            </div>

            <div className="comparison-section-title">Screening & Research</div>
            
            <div className="comparison-row">
              <div className="comparison-cell">Scan Type</div>
              <div className="comparison-cell">Manual (up to 10 tickers)</div>
              <div className="comparison-cell">By country , index, sector, industry, Top N stocks</div>
              <div className="comparison-cell">By country , index, sector, industry, Top N stocks</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Saved Scans</div>
              <div className="comparison-cell">5</div>
              <div className="comparison-cell">25</div>
              <div className="comparison-cell">100</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Stock Research Pages</div>
              <div className="comparison-cell">Basic</div>
              <div className="comparison-cell">Full</div>
              <div className="comparison-cell">Full + Interpretations</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Price Charts</div>
              <div className="comparison-cell">1Y</div>
              <div className="comparison-cell">1Y, 3Y, 5Y, 10Y</div>
              <div className="comparison-cell">1Y, 3Y, 5Y, 10Y</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Fundamentals Data</div>
              <div className="comparison-cell">Core valuation only</div>
              <div className="comparison-cell">Full metrics</div>
              <div className="comparison-cell">Full + Interpretations</div>
            </div>

            <div className="comparison-section-title">Portfolio Management</div>

            <div className="comparison-row">
              <div className="comparison-cell">Portfolios</div>
              <div className="comparison-cell">1</div>
              <div className="comparison-cell">5</div>
              <div className="comparison-cell">10</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Holdings per Portfolio</div>
              <div className="comparison-cell">10</div>
              <div className="comparison-cell">50</div>
              <div className="comparison-cell">200</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Active Alerts</div>
              <div className="comparison-cell">5</div>
              <div className="comparison-cell">25</div>
              <div className="comparison-cell">100</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Custom Alerts</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">One-Click Rebalancing</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-section-title">Validation & Strategy</div>

            <div className="comparison-row">
              <div className="comparison-cell">Historical return backtesting</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Strategy Lab</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Optimal Top N Finder</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Strategy Simulation (Balanced/Growth/Conservative)</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Written Investment Score Interpretations</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Written Fundamentals Interpretations</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Written Backtesting Interpretations</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-section-title">Reports & Support</div>

            <div className="comparison-row">
              <div className="comparison-cell">Weekly Digest</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">PDF Reports</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">Standard</div>
              <div className="comparison-cell">Advanced</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Email Alerts</div>
              <div className="comparison-cell">—</div>
              <div className="comparison-cell">✓</div>
              <div className="comparison-cell">✓</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell">Support</div>
              <div className="comparison-cell">FAQs</div>
              <div className="comparison-cell">48h</div>
              <div className="comparison-cell">Priority</div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="pricing-faq">
          <h2 className="pricing-faq-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, 
                and we'll prorate any charges or credits.
              </p>
            </div>

            <div className="faq-item">
              <h3>What payment methods do you accept?</h3>
              <p>
                We accept all major credit cards (Visa, Mastercard, American Express, Discover) 
                through our secure payment processor.
              </p>
            </div>

            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>
                Yes, you can cancel your subscription at any time. You'll continue to have access 
                until the end of your current billing period.
              </p>
            </div>

            <div className="faq-item">
              <h3>Can I try before upgrading?</h3>
              <p>
                Yes! Our Free Preview plan lets you manually scan up to 10 tickers at a time with no credit card required. 
                Upgrade to Pro or Premium anytime to unlock full S&P 500 scans and advanced research tools.
              </p>
            </div>

            <div className="faq-item">
              <h3>What's the difference between Pro and Premium?</h3>
              <p>
                Pro includes full S&P 500 screening, portfolios, alerts, and practical research tools. 
                Premium adds advanced research capabilities: Strategy Lab, written interpretations
                for scores and fundamentals, strategy simulation, and one-click portfolio rebalancing.
              </p>
            </div>

            <div className="faq-item">
              <h3>How does annual billing work?</h3>
              <p>
                Annual billing gives you about one month free compared to paying monthly. For example, 
                Pro annual is $209/year (effectively $17.41/month) versus $19/month billed monthly. 
                You're billed once per year and can cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="public-footer public-footer-compact">
        <div className="public-footer-container public-footer-container-compact">
          <Link to="/" className="footer-brand-compact">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="footer-brand-logo" 
            />
          </Link>
          <nav className="footer-nav-compact" aria-label="Footer navigation">
            <Link to="/" className="public-footer-link">Home</Link>
            <Link to="/pricing" className="public-footer-link">Pricing</Link>
            <Link to="/support" className="public-footer-link">Support</Link>
            <Link to="/privacy" className="public-footer-link">Privacy</Link>
            <Link to="/terms" className="public-footer-link">Terms</Link>
            <Link to="/investment-disclaimer" className="public-footer-link">Disclaimer</Link>
          </nav>
          <p className="footer-disclaimer footer-disclaimer-compact">Independent research tool. Not financial advice.</p>
        </div>
      </footer>
    </div>
  );
}

export default Pricing;
